import 'react-calendar-heatmap/dist/styles.css';

import { ExpandLessOutlined, ExpandMoreOutlined, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Grid, IconButton } from '@mui/material';
import { SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { dateFormatLongDate, getMonthsLabel, getWeekdayLabel, toDayEnd } from '@nx-smartmonkey/shared/helpers';
import moment from 'moment';
import { ReactElement, SyntheticEvent, cloneElement, useEffect, useState } from 'react';
import CalendarHeatmap from 'react-calendar-heatmap';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';

import { RoutalPalette } from '@nx-smartmonkey/ui';
import { intl } from '../../../helpers/IntGlobalProvider';
import { useRetrievePlansCalendar } from '../../../hooks/plan/useRetrievePlansCalendar';
import { useSearchPlansPredicates } from '../../../hooks/plan/useSearchPlansPredicates';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import Button from '../../../react-components/Button';
import Text from '../../../react-components/Text';
import './PlannerPlansCalendar.css';

const PlannerPlansCalendarContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 12px;
  border-radius: 10px;
  background-color: white;
  margin-bottom: 24px;
  border: 1px solid ${RoutalPalette.neutral10};
`;

const CALENDAR_RANGE = 24 * 60 * 60 * 1000 * 365;

const getCalendarRangeStart = (endDate: Date) => {
  const startDate = new Date(endDate);
  return new Date(startDate.setTime(startDate.getTime() - CALENDAR_RANGE));
};

const getCalendarRangeEnd = (endDate: Date) => {
  return new Date(endDate.setDate(endDate.getDate() + 1));
};

export const PlannerPlansCalendar = () => {
  const { data: user } = useRetrieveUser();
  const { predicates, setPredicates } = useSearchPlansPredicates();

  const plansCalendarExpanded = localStorage.getItem(`PLANS_HEATMAP_ID_EXPANDED`);
  const [isHeatmapExpanded, setIsHeatmapExpanded] = useState<boolean>(
    plansCalendarExpanded ? plansCalendarExpanded === `true` : true
  );

  const [selectedStartDate, setSelectedStartDate] = useState<Date>();
  const [selectedEndDate, setSelectedEndDate] = useState<Date>();
  const [mouseHoverDate, setMouseHoverDate] = useState<Date>();

  const initialEndDate = moment(new Date()).add(1, `month`).toDate();
  const [startDate, setStartDate] = useState<Date>(getCalendarRangeStart(initialEndDate));
  const [endDate, setEndDate] = useState<Date>(getCalendarRangeEnd(initialEndDate));

  const { data: plansData } = useRetrievePlansCalendar({
    startDate,
    endDate,
  });

  const calendarDates = getDatesInRange(startDate, endDate);
  const calendarValues = calendarDates.map((date: Date) => {
    const plansDataItem = plansData.find((item) => item.date === moment(date).format(`YYYY-M-D`));
    return {
      date: date.toDateString(),
      value: plansDataItem ? plansDataItem.value : 0,
    };
  });

  useEffect(() => {
    const startDatePredicate = predicates.find((p) => p.field === `execution_date` && p.operator === `gte`);
    setSelectedStartDate(
      startDatePredicate?.value
        ? new Date(startDatePredicate.value as string)
        : moment(new Date()).subtract(`1`, `month`).toDate()
    );
    const endDatePredicate = predicates.find((p) => p.field === `execution_date` && p.operator === `lte`);
    setSelectedEndDate(endDatePredicate?.value ? new Date(endDatePredicate.value as string) : new Date());
  }, [predicates]);

  return (
    <PlannerPlansCalendarContainer>
      <Grid container direction="column" style={{ padding: `12px 20px` }}>
        <Grid container item justifyContent="space-between" direction="row" wrap="nowrap">
          <Grid item container direction="row" justifyContent="flex-end" wrap="nowrap" alignItems="center">
            <Text intlId="planner.plans.calendar.legend_minus" />
            <div className="rectangle-0"></div>
            <div className="rectangle-1"></div>
            <div className="rectangle-2"></div>
            <div className="rectangle-3"></div>
            <div className="rectangle-4"></div>
            <Text intlId="planner.plans.calendar.legend_plus" />
            <div>
              <IconButton
                style={{ marginLeft: `15px`, height: `24px`, width: `24px`, padding: `0` }}
                onClick={() => {
                  setIsHeatmapExpanded(!isHeatmapExpanded);
                  localStorage.setItem(`PLANS_HEATMAP_ID_EXPANDED`, `${!isHeatmapExpanded}`);
                }}
              >
                {isHeatmapExpanded ? (
                  <ExpandLessOutlined style={{ width: `20px`, height: `20px` }} />
                ) : (
                  <ExpandMoreOutlined style={{ width: `20px`, height: `20px` }} />
                )}
              </IconButton>
            </div>
          </Grid>
        </Grid>
      </Grid>
      {isHeatmapExpanded && (
        <Grid
          direction="row"
          container
          justifyContent="center"
          alignItems="center"
          wrap="nowrap"
          style={{ padding: `0 20px ` }}
        >
          <Button
            id="calendar-heatmap-offset-less"
            styleType="primary"
            style={{ padding: 0, marginTop: `-30px`, borderRadius: `6px` }}
            onClick={() => {
              const newEndDate = moment(endDate).subtract(1, `month`).toDate();
              const newStartDate = getCalendarRangeStart(newEndDate);
              setStartDate(newStartDate);
              setEndDate(newEndDate);
            }}
          >
            <KeyboardArrowLeft />
          </Button>

          <CalendarHeatmap
            startDate={startDate.toDateString()}
            endDate={endDate.toDateString()}
            values={calendarValues.map((item) => {
              return {
                date: item.date,
                count: item.value,
              };
            })}
            showMonthLabels={true}
            showWeekdayLabels={true}
            monthLabels={getMonthsLabel({ language: user?.language.value })}
            weekdayLabels={getWeekdayLabel({ language: user?.language.value })}
            horizontal={true}
            classForValue={(value) => {
              if (value) {
                if (value.count > 4) {
                  return `color-scale-4`;
                } else {
                  return `color-scale-${value.count}`;
                }
              }

              return `color-empty`;
            }}
            tooltipDataAttrs={(value: any) => {
              if (!value) {
                return undefined;
              }
              return {
                'data-tip': `${intl.formatMessage(
                  { id: `planner.plans.num_plans` },
                  { total: value.count ?? 0 }
                )}, ${dateFormatLongDate(value.date)}`,
              };
            }}
            titleForValue={(value) => value?.date ?? ``}
            onClick={(value) => {
              if (!value) {
                console.warn(`Value is null!`);
                return;
              }

              if (selectedStartDate && selectedEndDate) {
                // Reset selection. Start again
                setSelectedStartDate(new Date(value.date));
                setSelectedEndDate(undefined);
                return;
              } else if (!selectedStartDate && !selectedEndDate) {
                // Start date selection
                setSelectedStartDate(new Date(value.date));
              } else if (selectedStartDate && !selectedEndDate) {
                if (new Date(value.date).getTime() < selectedStartDate.getTime()) {
                  // Start date selection. New time is less than the existing start date.
                  setSelectedStartDate(new Date(value.date));
                  setSelectedEndDate(undefined);
                  return;
                } else {
                  // End date selection
                  setSelectedEndDate(new Date(value.date));
                }
              } else if (!selectedStartDate && selectedEndDate) {
                setSelectedEndDate(new Date(value.date));
              }
              setPredicates([
                ...predicates.filter((p) => p.field !== `execution_date`),
                SearchPredicate.create({
                  field: `execution_date`,
                  operator: `gte`,
                  type: `date`,
                  value: (selectedStartDate ?? new Date(value.date)).toUTCString(),
                }),
                SearchPredicate.create({
                  field: `execution_date`,
                  operator: `lte`,
                  type: `date`,
                  value: toDayEnd(selectedEndDate ?? new Date(value.date)).toUTCString(),
                }),
              ]);
            }}
            onMouseOver={(_event: SyntheticEvent, value) => {
              if (selectedStartDate && value) {
                setMouseHoverDate(new Date(value.date));
              }
            }}
            gutterSize={6}
            transformDayElement={(element: ReactElement, value, index) => {
              let newProps = { ...element.props, rx: 2, ry: 2, className: `${element.props.className}` };

              if (
                // Check if is start
                selectedStartDate &&
                !selectedEndDate &&
                new Date(value?.date ?? ``).getUTCDate() === selectedStartDate.getUTCDate() &&
                new Date(value?.date ?? ``).getUTCMonth() === selectedStartDate.getUTCMonth()
              ) {
                newProps = {
                  ...element.props,
                  rx: 2,
                  ry: 2,
                  className: `${element.props.className} rect-selected-edge`,
                };
              } else if (
                new Date(value?.date ?? ``).getUTCDate() === new Date().getUTCDate() &&
                new Date(value?.date ?? ``).getUTCMonth() === new Date().getUTCMonth() &&
                new Date(value?.date ?? ``).getFullYear() === new Date().getFullYear()
              ) {
                newProps = {
                  ...element.props,
                  rx: 2,
                  ry: 2,
                  className: `${element.props.className} rect-current-day-edge`,
                };
              } else if (
                selectedStartDate &&
                mouseHoverDate &&
                !selectedEndDate &&
                new Date(value?.date ?? ``) >= selectedStartDate &&
                new Date(value?.date ?? ``) <= mouseHoverDate
              ) {
                // Paint hover
                newProps = {
                  ...element.props,
                  rx: 2,
                  ry: 2,
                  className: `${element.props.className} rect-selected`,
                };
              }

              // Paint date range selection
              else if (
                selectedStartDate &&
                selectedEndDate &&
                new Date(value?.date ?? ``) >= selectedStartDate &&
                new Date(value?.date ?? ``) <= selectedEndDate
              ) {
                newProps = {
                  ...element.props,
                  rx: 2,
                  ry: 2,
                  className: `${element.props.className} rect-selected`,
                };
              }
              return cloneElement({ ...element, props: newProps }, { title: value?.date });
            }}
          />
          <Button
            id="calendar-heatmap-offset-more"
            styleType="primary"
            style={{ padding: 0, marginTop: `-30px`, borderRadius: `6px` }}
            onClick={() => {
              const newEndDate = moment(endDate).add(1, `month`).toDate();
              const newStartDate = getCalendarRangeStart(newEndDate);
              setStartDate(newStartDate);
              setEndDate(newEndDate);
            }}
          >
            <KeyboardArrowRight />
          </Button>
          <ReactTooltip />
        </Grid>
      )}
    </PlannerPlansCalendarContainer>
  );
};
function getDatesInRange(startDate: Date, endDate: Date) {
  const dates: Date[] = [];
  const currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
}
